<template>
  <div class="profile">
    <!--
      WP-04V final closeout: the SAME `StudentAppHeader` Home and the Tutor use,
      not a page-specific header. `rendersStudentHeader` is `!isVolunteer` --
      deliberately the same condition `App/index.vue` and
      `AppSidebar/index.vue` use to suppress the legacy masthead for this
      route, and deliberately NOT `isStudentProfile`, so the legacy header is
      never suppressed without this one being rendered.
    -->
    <student-app-header
      v-if="rendersStudentHeader"
      :title="$t('appSidebar.sidebarLinks.studentProfile')"
      :ai-tutor-credits-remaining="aiTutorCreditsRemaining"
    />

    <!--
      TASK-SF-08 -- the ONE additive branch for the student نمایه (DESIGN-10).
      `isStudentProfile` is a LOCAL computed from existing user getters; no new
      store getter, and src/store/modules/user.js is NO CHANGE. The entire
      volunteer/legacy tree below renders unchanged for everyone else.
    -->
    <template v-if="isStudentProfile">
      <student-profile-edit
        v-if="studentEditing"
        @done="onStudentEditDone"
        @saved="onStudentSaved"
      />
      <student-profile-card
        v-else
        :key="studentCardKey"
        :saved-notice="studentJustSaved"
        @edit="onStudentEditRequested"
      />
    </template>

    <template v-else>
      <VerificationModal
        v-if="showSmsVerificationModal"
        data-testid="sms-verification-modal"
        :phoneOrEmailToVerify="phoneInputInfo.e164"
        :verificationMethod="VERIFICATION_METHOD.SMS"
        :closeModal="toggleShowSmsVerificationModal"
        :onCloseSuccess="updateVerifiedPhoneInfo"
      />
      <deactivate-account-modal
        v-if="showDeactivateAccountModal"
        :closeModal="toggleDeactivatedAccountModal"
        :setIsAccountActive="setIsAccountActive"
      />
      <div class="header">
        {{ $t('profile.header') }}
        <button
          type="button"
          class="editBtn btn"
          data-testid="edit-profile-btn"
          @click="editProfile()"
        >
          {{ editBtnMsg }}
        </button>
      </div>
      <div class="wrap-container">
        <div class="personal-info contain">
          <div v-if="errors.length" class="errors">
            <h4 class="errors-heading">
              {{ $t('profile.errors.heading-1') }}
            </h4>
            <ul class="errors-list">
              <li v-for="error in errors" v-bind:key="error">{{ error }}</li>
            </ul>
          </div>
          <div v-else-if="saveSuccess" class="success">
            {{ $t('profile.successMessage') }}
          </div>
          <div v-if="saveFailed" class="errors">
            <h4 class="errors-heading">{{ $t('profile.errors.heading-2') }}</h4>
          </div>
          <div class="subheader">{{ $t('profile.subHeader') }}</div>
          <div class="container-content">
            <div id="email" class="container-section">
              <div class="prompt">{{ $t('profile.email') }}</div>
              <div id="ph-no-capture" class="answer">{{ user.email }}</div>
            </div>
            <div>
              <div id="phone" class="container-section">
                <div class="prompt">{{ $t('profile.phoneNumber') }}</div>
                <div
                  id="ph-no-capture"
                  v-show="!activeEdit && user.phone"
                  class="answer phone-answer ltr"
                >
                  {{ internationalPhoneInfo.number }}

                  <!-- Unverified phone number: Show alert with CTA to verify phone number -->
                  <div
                    v-if="!user.phoneVerified"
                    class="alert alert-warning unverified"
                  >
                    {{ $t('profile.phoneVerifiedErrorMessage') }}
                    <span
                      class="uc-link secondary-btn"
                      @click.prevent="toggleShowSmsVerificationModal"
                      >{{ $t('profile.verify') }}
                    </span>
                  </div>
                </div>

                <div v-show="!activeEdit && !user.phone" class="answer">
                  {{ $t('profile.noPhoneProvided') }}
                </div>

                <vue-phone-number-input
                  class="phone-input"
                  v-show="activeEdit"
                  v-model="phoneNational"
                  :default-country-code="internationalPhoneInfo.country"
                  :error="
                    invalidInputs.indexOf('phone') > -1 &&
                      !phoneInputInfo.isValid
                  "
                  :required="true"
                  color="#555"
                  valid-color="#16ba97"
                  @update="onPhoneInputUpdate"
                />

                <div class="sms-consent" v-if="shouldSeeSmsConsentCheckbox">
                  <checkbox
                    id="sms-consent-checkbox"
                    :disabled="!activeEdit"
                    v-model="smsConsent"
                    @change="emitSmsConsentCheckboxChangedEvent"
                  />
                  <label for="sms-consent-input">{{
                    $t('profile.smsConsentLabel')
                  }}</label>
                </div>
                <div class="description" v-if="user.isVolunteer">
                  {{ $t('profile.smsConsentDescription') }}
                </div>
              </div>
            </div>

            <div v-if="user.isVolunteer" class="container-section">
              <div class="prompt">
                {{ $t('profile.isVolunteer.subHeader') }}
              </div>
              <div class="answer">
                <toggle-button
                  :disabled="!activeEdit"
                  :value="isAccountActive"
                  :labels="{ checked: 'Active', unchecked: 'Deactivated' }"
                  :width="95"
                  :color="{
                    checked: '#16D2AA',
                    unchecked: '#F44747',
                    disabled: '#AAAAAA',
                  }"
                  @change="toggleAccountActive"
                  :sync="true"
                  data-testid="deactivate-account-toggle"
                />
              </div>
              <div class="description">
                {{ $t('profile.isVolunteer.description') }}
              </div>
            </div>

            <div
              v-if="isNotificationPermissionGranted"
              class="container-section"
            >
              <div class="prompt">
                {{ $t('profile.isNotificationPermissionGranted.subHeader') }}
              </div>
              <div class="answer">
                <toggle-button
                  :value="isAllowingNotifications"
                  :labels="{ checked: 'On', unchecked: 'Off' }"
                  :width="95"
                  :color="{
                    checked: '#16D2AA',
                    unchecked: '#F44747',
                    disabled: '#AAAAAA',
                  }"
                  @change="toggleWebNotifications"
                  :sync="true"
                />
              </div>
              <div v-if="user.isVolunteer" class="description">
                {{
                  $t(
                    'profile.isNotificationPermissionGranted.volunteerDescription'
                  )
                }}
              </div>
              <div v-else class="description">
                {{ $t('profile.isNotificationPermissionGranted.description') }}
              </div>
            </div>

            <div class="container-section resetBtn">
              <router-link
                :to="`/${$i18n.locale}/resetpassword`"
                class="prompt"
                >{{ $t('profile.reset') }}</router-link
              >
            </div>
          </div>
        </div>

        <div v-if="user.isVolunteer" class="cert-info contain">
          <div class="subheader-subjects">
            <div
              v-if="isMutedSubjectAlertsActive"
              class="subheader-subjects--left"
            >
              {{ $t('profile.unlockedSubjects') }}
            </div>
            <div v-else class="subheader-subjects--center">
              {{ $t('profile.unlockedSubjects') }}
            </div>
            <div
              v-if="isMutedSubjectAlertsActive"
              class="subheader-subjects--right"
            >
              {{ $t('profile.tutoringAlerts') }}
            </div>
          </div>
          <loader
            v-if="isFetchingSubjects"
            class="loader--center"
            :height="40"
            :width="40"
          />
          <div v-else class="container-content cert">
            <div
              v-for="(value, key) in userSubjects"
              :key="`certification-${key}-${value}`"
            >
              <div v-if="value" class="certBox">
                <div class="subjects-left">
                  <div
                    :style="{ backgroundColor: subjects[key].topicColor }"
                    class="certKey"
                  >
                    {{ subjects[key].topicDisplayName.toUpperCase() }}
                  </div>
                  <div class="certValue">
                    {{ subjects[key].displayName }}
                  </div>
                </div>
                <div v-if="isMutedSubjectAlertsActive" class="subjects-right">
                  <toggle-button
                    :disabled="!activeEdit"
                    :value="subjectIsNotMuted(key)"
                    :width="75"
                    :labels="{ checked: 'On', unchecked: 'Off' }"
                    :color="{
                      checked: '#16D2AA',
                      unchecked: '#F44747',
                      disabled: '#AAAAAA',
                    }"
                    @change="
                      togglemutedSubjectAlerts(key, subjectIsNotMuted(key))
                    "
                    :sync="true"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import PhoneNumber from 'awesome-phonenumber'
import { mapGetters, mapState } from 'vuex'
import UserService from '@/services/UserService'
import AnalyticsService from '@/services/AnalyticsService'
import DeactivateAccountModal from './DeactivateAccountModal'
import setNotificationPermission from '@/utils/set-notification-permission'
import getNotificationPermission from '@/utils/get-notification-permission'
import VuePhoneNumberInput from 'vue-phone-number-input'
import { EVENTS, VERIFICATION_METHOD } from '@/consts'
import Loader from '@/components/Loader.vue'
import VerificationModal from './VerificationModal.vue'
import Checkbox from '@/components/CheckBox.vue'
import StudentProfileCard from '@/components/StudentProfile/StudentProfileCard.vue'
import StudentProfileEdit from '@/components/StudentProfile/StudentProfileEdit.vue'
import StudentAppHeader from '@/components/StudentShell/StudentAppHeader.vue'

// Student Journey V2 -- the AI Tutor setup's «ویرایش» opens this page with
// `?from=ai-tutor&intent=<mode>`. Only these two symbolic values are honoured
// (never a caller-supplied URL), so the return can only ever land on the tutor.
const RETURN_TO_TUTOR = 'ai-tutor'
const TUTOR_INTENTS = ['troubleshoot', 'review', 'learn']

export default {
  name: 'profile-view',
  components: {
    Checkbox,
    DeactivateAccountModal,
    VuePhoneNumberInput,
    Loader,
    VerificationModal,
    StudentProfileCard,
    StudentProfileEdit,
    StudentAppHeader,
  },
  data() {
    return {
      activeEdit: false,
      // TASK-SF-09: the student subtree's own edit flag, deliberately
      // separate from the volunteer `activeEdit` machinery. `studentCardKey`
      // forces the read card to remount (and refetch) after a save.
      studentEditing: false,
      studentCardKey: 0,
      // Transient save-success handoff: set once a PATCH succeeds so the
      // remounted read card can show an accessible «تغییرات ذخیره شد.»
      // notice. Student-only; the legacy volunteer branch never reads this.
      studentJustSaved: false,
      editBtnMsg: this.$root.$t('profile.edit'),
      errors: [],
      invalidInputs: [],
      saveFailed: false,
      saveSuccess: false,
      phoneNational: '',
      phoneInputInfo: {},
      isAccountActive: true,
      isAllowingNotifications: true,
      showDeactivateAccountModal: false,
      shouldSeeSmsConsentCheckbox: false,
      showSmsVerificationModal: false,
      smsConsent: false,
      newMutedSubjectAlerts: [],
    }
  },
  created() {
    // Coming from the tutor setup lands straight on the edit form, as the
    // reference does -- the student is here to change one thing and go back.
    if (this.returnsToTutor && this.isStudentProfile) this.studentEditing = true
    this.isAllowingNotifications = getNotificationPermission() === 'granted'
    this.isAccountActive = !this.user.isDeactivated
    if (this.user.phone) {
      const num =
        this.user.phone[0] === '+' ? this.user.phone : `+1${this.user.phone}`
      const pn = new PhoneNumber(num)
      this.phoneNational = pn.getNumber('national')

      // Hack to initially mock the vue-phone-number-input data
      this.phoneInputInfo = {
        isValid: true,
        e164: pn.getNumber('e164'),
      }

      this.smsConsent = this.user.smsConsent
    }
    if (this.isMutedSubjectAlertsActive && this.user.isVolunteer) {
      this.newMutedSubjectAlerts = [...this.user.mutedSubjectAlerts]
    }
    this.setFlagsForEditingPhoneNumber()
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
      subjects: state => state.subjects.subjects,
      isFetchingSubjects: state => state.subjects.isFetchingSubjects,
    }),
    ...mapGetters({
      avatarUrl: 'user/avatarUrl',
      allSubtopics: 'subjects/allSubtopics',
      isFilterActiveSubjectsActive: 'featureFlags/isFilterActiveSubjectsActive',
      isMutedSubjectAlertsActive: 'featureFlags/isMutedSubjectAlertsActive',
    }),
    // CR-03 (doc 28 §16/§17): Subway's Student AI Tutor credit balance for the
    // header pill; null (unknown / not a Student) hides it.
    aiTutorCreditsRemaining() {
      const credits = this.$store.getters['user/aiTutorCreditsRemaining']
      return typeof credits === 'number' ? credits : null
    },
    name() {
      const user = this.$store.state.user.user
      return user.firstname || (user.isVolunteer ? 'volunteer' : 'student')
    },
    // TASK-SF-08: local computed from EXISTING getters -- no new store getter,
    // src/store/modules/user.js is NO CHANGE. A dual-role user acting as a
    // volunteer sees the legacy tree; the student read view is for a
    // student-role account not currently acting as a volunteer.
    returnsToTutor() {
      return this.$route.query.from === RETURN_TO_TUTOR
    },
    isStudentProfile() {
      return (
        this.$store.getters['user/hasStudentRole'] &&
        !this.$store.getters['user/isVolunteer']
      )
    },
    // WP-04V final closeout: the acting-context gate the App-level legacy-header
    // suppression uses (`!isVolunteer`). Kept separate from `isStudentProfile`
    // above on purpose -- see the template comment.
    rendersStudentHeader() {
      return !this.$store.getters['user/isVolunteer']
    },
    VERIFICATION_METHOD() {
      return VERIFICATION_METHOD
    },
    internationalPhoneInfo() {
      if (!this.user.phone) return false

      const num =
        this.user.phone[0] === '+' ? this.user.phone : `+1 ${this.user.phone}`
      const pn = new PhoneNumber(num)

      return {
        number: pn.getNumber('international'),
        country: pn.getRegionCode(),
      }
    },
    certKey() {
      let subtopicObj = {}

      for (let [topicName, topicData] of Object.entries(this.subjects)) {
        for (let topic in topicData.subtopics) {
          if (
            Object.prototype.hasOwnProperty.call(topicData.subtopics, topic)
          ) {
            const { displayName } = topicData.subtopics[topic]
            let topicDisplay = topicName
            // TODO: remove when `readingWriting` is updated to have `english` as a key
            if (topicDisplay === 'readingWriting') topicDisplay = 'english'
            subtopicObj[displayName] = topicDisplay.toUpperCase()
          }
        }
      }
      return subtopicObj
    },
    userSubjects() {
      const user = this.$store.state.user.user
      const userSubjects = this.isFilterActiveSubjectsActive
        ? user.activeSubjects
        : user.subjects

      const subjects = {}
      for (const subject of userSubjects) {
        subjects[subject] = true
      }
      return subjects
    },
    isNotificationPermissionGranted() {
      return 'Notification' in window && Notification.permission === 'granted'
    },
    userNeedsToVerifyPhone() {
      return (
        this.user.phone !== this.phoneInputInfo.e164 ||
        (this.user.phone && !this.user.phoneVerified)
      )
    },
  },
  methods: {
    onPhoneInputUpdate(phoneInputInfo) {
      this.phoneInputInfo = phoneInputInfo
    },

    // TASK-SF-08: the student read card's «ویرایش» affordance. TASK-SF-09
    // replaces this stub with the student edit subtree (DESIGN-11); it is
    // deliberately separate from the volunteer `activeEdit` machinery and
    // `createUpdateProfileRequestBody()`, which stay the volunteer path.
    onStudentEditRequested() {
      this.studentEditing = true
      // P39 TR-029: an edit starts a new operation, so the previous save's
      // notice is spent. Without this, «بازگشت» remounts the read card with
      // the old flag still true and «تغییرات ذخیره شد.» shows again.
      this.studentJustSaved = false
    },
    onStudentSaved() {
      if (this.returnsToTutor) {
        this.returnToTutor()
        return
      }
      this.studentEditing = false
      this.studentCardKey += 1
      this.studentJustSaved = true
    },
    // «بازگشت» from the edit form: back to the read card normally, back to the
    // tutor setup when that is where the student came from.
    onStudentEditDone() {
      if (this.returnsToTutor) {
        this.returnToTutor()
        return
      }
      this.studentEditing = false
    },
    // `replace`, not `push`: history stays a straight line (dashboard, setup)
    // instead of gaining a profile hop. The setup re-reads the profile on
    // mount, so the study field it shows is the one just saved.
    returnToTutor() {
      const intent = this.$route.query.intent
      this.$router.replace({
        name: 'StudentAiTutorView',
        params: { lang: this.$route.params.lang },
        query: TUTOR_INTENTS.includes(intent) ? { intent } : {},
      })
    },

    async emitSmsConsentCheckboxChangedEvent() {
      AnalyticsService.captureEvent(EVENTS.SMS_CONSENT_CHECKBOX_CHANGED, {
        event: EVENTS.SMS_CONSENT_CHECKBOX_CHANGED,
        value: this.smsConsent ? 'checked' : 'unchecked',
      })
    },

    toggleAccountActive({ value }) {
      if (!value) {
        this.toggleDeactivatedAccountModal()
        return
      }
      this.setIsAccountActive(true)
    },

    toggleDeactivatedAccountModal() {
      this.showDeactivateAccountModal = !this.showDeactivateAccountModal
    },

    toggleWebNotifications({ value }) {
      const permission = value ? 'granted' : 'denied'
      this.isAllowingNotifications = value
      setNotificationPermission(permission)
    },

    togglemutedSubjectAlerts(subject, isNotMuted) {
      if (isNotMuted) {
        this.newMutedSubjectAlerts.push(subject)
      } else {
        this.newMutedSubjectAlerts = this.newMutedSubjectAlerts.filter(
          s => s != subject
        )
      }
    },

    subjectIsNotMuted(subject) {
      return !this.newMutedSubjectAlerts.includes(subject)
    },

    toggleShowSmsVerificationModal() {
      this.showSmsVerificationModal = !this.showSmsVerificationModal
    },

    setIsAccountActive(value) {
      this.isAccountActive = value
    },

    async updateVerifiedPhoneInfo() {
      this.shouldSeeSmsConsentCheckbox = !this.user.isVolunteer
    },

    /**
     * Toggle editing state.
     * {Case A} if activeEdit === false: enter the editing state by setting activeEdit to true
     * {Case B} if activeEdit === true: save profile changes & exit the editing state by setting activeEdit to false
     */
    editProfile() {
      // {Case A} Enter the editing state, then early exit
      if (!this.activeEdit) {
        this.editBtnMsg = this.$root.$t('profile.save')
        this.activeEdit = true
        this.saveSuccess = false
        return
      }

      // {Case B} The remainder of this function saves new changes and exits the editing state

      // Start by erasing previous errors
      this.errors = []
      this.invalidInputs = []
      this.saveFailed = false
      this.saveSuccess = false

      // Validate fields
      if (this.user.isVolunteer) {
        // volunteers must provide a phone number, so display error message and
        // mark field invalid
        if (!this.phoneInputInfo.isValid || !this.phoneInputInfo.e164) {
          this.errors.push(this.$root.$t('profile.phoneValidationErrorMessage'))
          this.invalidInputs.push(this.$root.$t('profile.phone'))
        }
      } else if (this.phoneInputInfo.e164 && !this.phoneInputInfo.isValid) {
        this.errors.push(this.$root.$t('profile.phoneValidationErrorMessage'))
        this.invalidInputs.push(this.$root.$t('profile.phone'))
      }

      if (!this.errors.length) {
        const isDeactivatedBeforeUpdate = this.user.isDeactivated
        const reqBody = this.createUpdateProfileRequestBody()
        const phoneChanged = Object.prototype.hasOwnProperty.call(
          reqBody,
          'phone'
        )

        // wait for save to succeed before coming out of edit mode
        UserService.setProfile(reqBody).then(
          () => {
            this.editBtnMsg = this.$root.$t('profile.edit')
            this.activeEdit = false
            this.saveFailed = false
            this.saveSuccess = true

            AnalyticsService.captureEvent(EVENTS.PROFILE_UPDATED, {
              event: EVENTS.PROFILE_UPDATED,
            })

            if (
              reqBody.isDeactivated &&
              reqBody.isDeactivated !== isDeactivatedBeforeUpdate
            )
              AnalyticsService.captureEvent(EVENTS.ACCOUNT_DEACTIVATED, {
                event: EVENTS.ACCOUNT_DEACTIVATED,
              })

            if ('smsConsent' in reqBody) {
              const evt = reqBody.smsConsent
                ? EVENTS.SMS_OPTED_IN
                : EVENTS.SMS_OPTED_OUT
              AnalyticsService.captureEvent(evt, {
                event: evt,
              })
            }

            // Update user state after successful API call
            let addToUserData = {
              phone: reqBody.phone ?? this.user.phone,
              isDeactivated: reqBody.isDeactivated ?? !this.isAccountActive,
              smsConsent: reqBody.smsConsent ?? this.user.smsConsent,
            }
            if (phoneChanged) {
              addToUserData.phoneVerified = false
            }
            if (this.isMutedSubjectAlertsActive && this.user.isVolunteer) {
              addToUserData.mutedSubjectAlerts =
                reqBody.mutedSubjectAlerts ?? this.user.mutedSubjectAlerts
            }
            this.$store.dispatch('user/addToUser', addToUserData).then(() => {
              if (phoneChanged && this.phoneInputInfo.e164) {
                const updatedPhone = new PhoneNumber(this.phoneInputInfo.e164)
                this.phoneNational = updatedPhone.getNumber('national')
              }
              this.setFlagsForEditingPhoneNumber()
              // Phone number verification flow
              if (phoneChanged || this.userNeedsToVerifyPhone) {
                this.showSmsVerificationModal = true
              }
            })
          },
          () => {
            this.saveFailed = true
            this.saveSuccess = false
          }
        )
      }
    },

    createUpdateProfileRequestBody() {
      const reqBody = {}
      reqBody.isDeactivated = !this.isAccountActive

      // Students get a checkbox to opt in/out of SMS
      if (!this.user.isVolunteer && this.smsConsent !== this.user.smsConsent) {
        reqBody.smsConsent = this.smsConsent
      }

      const hasPhoneInput = Object.keys(this.phoneInputInfo || {}).length > 0
      const normalizedUserPhone = this.user.phone
        ? this.user.phone[0] === '+'
          ? this.user.phone
          : `+1${this.user.phone}`
        : ''

      if (
        hasPhoneInput &&
        this.phoneInputInfo.e164 &&
        this.phoneInputInfo.isValid &&
        this.phoneInputInfo.e164 !== normalizedUserPhone
      ) {
        reqBody.phone = this.phoneInputInfo.e164
      }

      if (this.isMutedSubjectAlertsActive && this.user.isVolunteer) {
        reqBody.mutedSubjectAlerts = this.newMutedSubjectAlerts
      }
      return reqBody
    },

    setFlagsForEditingPhoneNumber() {
      this.shouldSeeSmsConsentCheckbox =
        !this.user.isVolunteer && this.user.phone
    },
  },
}
</script>

<style lang="scss" scoped>
.profile {
  font-size: 16px;
  font-family: $font-family-default;
}

.wrap-container {
  padding: 15px 15px 55px 15px;
  @include flex-container(column);
  align-items: stretch;

  @include child-spacing(top, 16px);
  @include child-spacing(right, 0);

  @include breakpoint-above('large') {
    padding: 40px;

    @include child-spacing(top, 0);
    @include child-spacing(right, 40px);

    @include flex-container(row);

    & > * {
      flex-basis: 50%;
    }
  }
}

.header {
  display: flex;
  margin: 0;
  font-size: 24px;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  padding: 25px 15px 10px 35px;

  @include breakpoint-above('large') {
    padding: 40px 40px 0 40px;
  }
}

.contain {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: #fff;
}

.container-content {
  padding: 20px;
  text-align: left;

  @include breakpoint-above('large') {
    padding: 40px;
  }
}

.subheader {
  font-weight: 600;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px 15px 0;
  font-size: 20px;
}

.subheader-subjects {
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1;
  font-size: 20px;
  font-weight: 600;
  padding: 30px 15px 0;
}

.subheader-subjects--center {
  grid-column: 1 / 3;
  text-align: center;
}

.subheader-subjects--left {
  grid-column: 1;
  text-align: left;
}

.subheader-subjects--right {
  grid-column: 2;
  text-align: right;
}

.subjects-left {
  grid-column: 1;
  text-align: left;
}

.subjects-right {
  grid-column: 2;
  text-align: right;
  padding-top: 8px;
}

.container-section {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

ul {
  padding: 0px;
  height: 100%;
  margin: auto;
}

.basic-info {
  display: flex;
  flex-direction: column;
  align-items: left;
  margin-top: 30px;
}

.info-header {
  display: flex;
  align-items: center;

  margin-left: 30px;
  font-size: 20px;
  font-weight: 600;
}

.info-header.basic {
  height: 60px;
}

.info-header.cert {
  margin-bottom: 15px;
}

.answer {
  font-weight: 600;
}

.phone-answer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: baseline;
}

.answer .vue-js-switch {
  margin: 5px 0;
}

.answer ul {
  margin-left: 20px;
}

.phone-input {
  margin: 5px 0 0;
}

.description {
  margin-top: 10px;
  font-size: 12px;
}

button {
  height: 30px;
  border-radius: 20px;
  padding: 0px 10px;
  color: #16d2aa;
  background-color: #f6f6f6;
}

button:active,
button:hover {
  background-color: #16d2aa;
  color: #fff;
}

.editBtn {
  background-color: #16d2aa;
  border-radius: 30px;
  width: 120px;
  align-items: center;
  height: 40px;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  color: white;

  &:hover {
    color: #2c3e50;
  }
}

.form-control {
  border: none;
  box-shadow: none;
  border-radius: 0;
  background-color: #f0f8fd;
}

.form-control {
  border-bottom: 3px solid #16d2aa;
  margin-bottom: 10px;

  &.invalid {
    border-bottom: 3px solid #bf0000;
  }
}

.form-control:focus {
  border-bottom: 3px solid #16d2aa;
  box-shadow: none;
}

.resetBtn {
  padding: 20px 0 5px;
  a {
    color: darken($c-error-red, 25%);

    &:hover {
      text-decoration: none;
      color: darken($c-error-red, 40%);
    }
  }
}

.container-content.cert {
  padding: 30px 0 0;
}

.certBox {
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1;
  height: 60px;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
  border-top: 1px solid #cccccf;
  font-weight: 600;
}

.certKey {
  display: inline-block;
  border-radius: 12px;
  padding: 0 10px;
  margin: 0 10px 0 0;
  color: #ffffff;
  font-size: 12px;
  background-color: #1855d1;
}

.certValue {
  display: inline-block;
}

.errors {
  text-align: left;
  padding: 30px;
}

.success {
  text-align: left;
  padding: 30px;
  background-color: $c-success-green;
  color: #fff;
  border-radius: 8px;
}

.errors-heading {
  color: #bf0000;
}

.errors-list {
  color: #bf0000;
  margin-left: 40px;
}

.loader--center {
  margin-top: 2em;
  margin-right: auto;
  margin-left: auto;
}

.unverified {
  display: flex;
  flex-direction: row;
  gap: 4px;
  font-weight: normal;
  g {
    fill: $c-warning-orange;
  }
}

.secondary-btn {
  &:hover {
    cursor: pointer;
  }

  p {
    font-style: none;
  }
}

.sms-consent {
  display: flex;
  align-items: baseline;
}

html.is-rtl {
  .profile {
    font-family: 'IRANSans';
  }

  .wrap-container {
    @include child-spacing-rtl(right, 0);

    @include breakpoint-above('large') {
      @include child-spacing-rtl(right, 40px);
    }
  }

  .container-content {
    text-align: right;
  }

  .subheader-subjects--left {
    text-align: right;
  }

  .subheader-subjects--right {
    text-align: left;
  }

  .subjects-left {
    text-align: right;
  }

  .subjects-right {
    text-align: left;
  }

  .basic-info {
    align-items: right;
  }

  .info-header {
    margin-right: 30px;
    margin-left: unset;
  }

  .answer ul {
    margin-right: 20px;
    margin-left: unset;
  }

  .certKey {
    margin: 0 0 0 10px;
  }

  .errors {
    text-align: right;
    padding: 30px;
  }

  .errors-list {
    margin-right: 40px;
    margin-left: unset;
  }
}
</style>
